import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, pickLang } from "../contexts/LanguageContext";
import { T } from "../i18n/strings";

export function ArticleCard({ article, variant = "default" }) {
  const { lang } = useLanguage();
  const title = pickLang(article, "title", lang);
  const dek = pickLang(article, "dek", lang);
  const sectionLabel = T.nav[article.section]?.[lang] || article.section;
  const dateStr = new Date(article.published_at).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", {
    year: "numeric", month: "short", day: "numeric",
  });

  if (variant === "lead") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block group pw-fade-up"
        data-testid={`article-card-lead-${article.slug}`}
      >
        <div className="pw-window-hero overflow-hidden">
          <img
            src={article.hero_image_url}
            alt={title}
            className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="mt-6">
          <div className="pw-eyebrow mb-3" data-testid="card-section">
            {sectionLabel} · {T.labels.lead[lang]}
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#081422] leading-[1.05] tracking-tight group-hover:text-[#0F2137] transition-colors">
            {title}
          </h2>
          <p className="font-serif-body text-lg sm:text-xl text-[#475569] mt-4 leading-relaxed">{dek}</p>
          <div className="flex items-center gap-4 mt-5 font-mono-meta text-[0.7rem] uppercase tracking-widest text-[#64748B]">
            <span>{article.author_name}</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>{dateStr}</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>{article.reading_time} {T.labels.minRead[lang]}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 py-4 border-b border-[#081422]/10 pw-card-hover"
        data-testid={`article-card-compact-${article.slug}`}
      >
        <img
          src={article.hero_image_url}
          alt={title}
          className="w-24 h-24 object-cover flex-shrink-0"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <div className="pw-eyebrow mb-1 text-[0.65rem]">{sectionLabel}</div>
          <h3 className="font-serif-display text-lg font-semibold text-[#081422] leading-snug group-hover:text-[#C5A059] transition-colors">
            {title}
          </h3>
          <div className="font-mono-meta text-[0.65rem] uppercase tracking-widest text-[#64748B] mt-2">
            {article.reading_time} {T.labels.minRead[lang]}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block pw-card-hover pw-fade-up"
      data-testid={`article-card-${article.slug}`}
    >
      <div className="pw-window-frame overflow-hidden p-0">
        <div className="overflow-hidden">
          <img
            src={article.hero_image_url}
            alt={title}
            className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="p-5">
          <div className="pw-eyebrow mb-2">{sectionLabel}</div>
          <h3 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#081422] leading-snug group-hover:text-[#0F2137] transition-colors">
            {title}
          </h3>
          {dek && (
            <p className="font-serif-body text-[#475569] mt-2 leading-relaxed line-clamp-2">{dek}</p>
          )}
          <div className="flex items-center gap-3 mt-4 font-mono-meta text-[0.65rem] uppercase tracking-widest text-[#64748B]">
            <span>{article.author_name}</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>{article.reading_time} {T.labels.minRead[lang]}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default ArticleCard;
